import { useMutation, useQueryClient } from "@tanstack/react-query";
import { taskApi } from "api";
import { automaticThoughtIdType } from "api/conceptApi";
import { DASHBOARD_DETAIL_ENDPOINT, DASHBOARD_DOMAIN } from "api/dashboardApi";
import { HOME_DOMAIN, HOME_MEDITATION_LIST_ENDPOINT } from "api/homeApi";
import {
  MEDITATION_RECORD_DOMAIN,
  MEDITATION_RECORD_LIST_ENDPOINT,
  meditationRecordApi,
} from "api/meditationRecordApi";
import { selfCheckApi } from "api/selfCheckApi";
import {
  THOUGHTRECORD_DOMAIN,
  THOUGHTRECORD_LIST_ENDPOINT,
  thoughtRecordApi,
} from "api/thoughtRecordApi";
import { ProgramContentType } from "data/BlockComponent";
import { PercentageType } from "data/CellComponent";
import useRefetchTaskList from "hooks/useRefetchTaskList";
import useSaveMeditationRecordWithRawContent from "hooks/useSaveMeditationRecordWithRawContent";
import useSaveThoughtRecordWithRawContent from "hooks/useSaveThoughtRecordWithRawContent";
import useSubmitImpactScore from "hooks/useSubmitImpactScore";
import {
  getReferenceData,
  getSelfCheckScore,
  getTaskIdFromTaskKey,
  isAllUserFieldFilled,
  isBlockUserFieldFilled,
  isLineHidden,
} from "logic/logics";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { layoutAtom } from "recoil/layoutAtom";
import { taskListAtom } from "recoil/taskListAtom";
import { userAtom } from "recoil/userAtom";
import { doneButtonLoadingAtom } from "recoil/doneButtonLoadingAtom";
import useRemoveTab from "hooks/useRemoveTab";
import { doneButtonTmpDisabledAtom } from "recoil/doneButtonTmpDisabledAtom";
import { invalidTextareaIndexAtom } from "recoil/invalidTextareaIndexAtom";
import { Box, Button, CircularProgress } from "@mui/joy";
import CustomModal from "../../common/CustomModal";
import useIsInappWebview from "hooks/useIsInappWebview";
import useDetectIOS from "hooks/useDetectIOS";
import { CheckIconSvgDoneButton } from "../../../static/image/common/SvgAssets";
import { getDoneModalContent } from "../../../utils/modalUtil";
import { homeThoughtRecordListAtom } from "../../../recoil/homeThoughtRecordListAtom";
import { useBlocker } from "react-router-dom";
import { sendMessage } from "../../../utils/webview";

export default function DoneButton({
  taskKey,
  thoughtRecordKey,
  activityRecordKey,
  meditationRecordKey,
  data,
  doneWithEnter,
  setDoneWithEnter,
  setMoveToIndex,
  isDone,
  isOpen,
  size,
  onUndoneSuccess,
}: {
  taskKey: string;
  thoughtRecordKey?: string;
  activityRecordKey?: string;
  meditationRecordKey?: string;
  data?: ProgramContentType[];
  doneWithEnter?: boolean;
  setDoneWithEnter?: React.Dispatch<React.SetStateAction<boolean>>;
  setMoveToIndex?: React.Dispatch<React.SetStateAction<number | undefined>>;
  isDone?: boolean;
  isOpen?: boolean;
  size?: "sm" | "lg";
  onUndoneSuccess?: () => void;
}) {
  const isFirstDone = data?.every((element) => !element.indentation);
  const { t: t_ui } = useTranslation("translation", {
    keyPrefix: "ui",
  });
  const isSelfCheck =
    getTaskIdFromTaskKey(`${taskKey}`) === "0-1-A" ||
    getTaskIdFromTaskKey(`${taskKey}`) === "0-2-A" ||
    getTaskIdFromTaskKey(`${taskKey}`) === "2-3" ||
    getTaskIdFromTaskKey(`${taskKey}`) === "2-4" ||
    getTaskIdFromTaskKey(`${taskKey}`) === "102-1-A" ||
    getTaskIdFromTaskKey(`${taskKey}`) === "102-2-A";

  const isThoughtRecord =
    taskKey?.includes("2-2-T") && thoughtRecordKey !== undefined;
  const isMeditationRecord =
    taskKey?.includes("5-7-T") && meditationRecordKey !== undefined;

  const [taskListState, setTaskListState] = useRecoilState(taskListAtom);
  const homeThoughtRecordList = useRecoilValue(homeThoughtRecordListAtom);

  const setLayoutState = useSetRecoilState(layoutAtom);
  const [isLoading, setIsLoading] = useRecoilState(doneButtonLoadingAtom);
  const [isTmpDisabled, setIsTmpDisabled] = useRecoilState(
    doneButtonTmpDisabledAtom
  );

  const setInvalidTextareaIndexList = useSetRecoilState(
    invalidTextareaIndexAtom
  );

  const [isBackGestureDetected, setBackGestureDetected] = useState(false);

  const [doneModalContent, setDoneModalContent] = useState({
    title: "",
    subtitle: "",
  });

  const isInappWebview = useIsInappWebview();
  const { isIOS } = useDetectIOS();

  const focusElement = useCallback(
    (el: HTMLElement) => {
      el.focus();
      try {
        const input = el as HTMLInputElement | HTMLTextAreaElement;
        const len = input.value?.length || 0;
        input.setSelectionRange?.(len, len);
      } catch {}

      if (isIOS) {
        try {
          el.click();
        } catch {}
      }
    },
    [isIOS]
  );

  const taskId = getTaskIdFromTaskKey(taskKey);
  const automaticThoughtId = taskId.includes("_")
    ? (taskId.split("_")[1] as automaticThoughtIdType)
    : undefined;

  const user = useRecoilValue(userAtom);
  const isPatient = user?.role === "patient";
  const lastTaskUpdatedAt = user?.lastTaskUpdatedAt; // e.g. 2025-07-12 12:00:00

  const { removeTab } = useRemoveTab({ taskKey });

  const [isDirty, setDirty] = useState(false);

  // const blocker = useBlocker(({ currentLocation, nextLocation }) => {
  //   return isDirty && currentLocation.pathname !== nextLocation.pathname;
  // });

  const disabled =
    isDone === undefined ||
    !isAllUserFieldFilled(data, false, user) ||
    (isPatient && isDone && !isOpen && !isMeditationRecord && !isThoughtRecord);

  const isLastDone = isThoughtRecord
    ? homeThoughtRecordList
        .filter(
          (element) =>
            element.thoughtRecordKey !== thoughtRecordKey && element.isOpen
        )
        .every((element) => element.isDone) &&
      taskListState.every((element) => element.isDone)
    : taskListState
        .filter((element) => element.taskKey !== taskKey)
        .every((element) => element.isDone) &&
      homeThoughtRecordList
        .filter((element) => element.isOpen)
        .every((element) => element.isDone);

  const queryClient = useQueryClient();

  const saveSelfCheckQueryFn = useCallback(
    () => selfCheckApi.save(getSelfCheckScore(data)),
    [data]
  );

  const highlightUnfilledBlocks = () => {
    if (disabled && data) {
      const textareaIndices: string[] = [];
      const isCoach = (user?.accessToken && !isPatient) || false;

      data.forEach((block, bIdx) => {
        const isBlockHidden =
          block.isHidden ||
          block.isAlwaysHidden ||
          (isCoach && !block.isShown && block.hideIfCoach) ||
          (!isCoach && (!block.isShown || block.hideIfPatient));

        if (!isBlockHidden && !isBlockUserFieldFilled(block, data, user)) {
          block.lines.forEach((line, lIdx) => {
            if (!isLineHidden(data, block, line)) {
              line.forEach((cell, cIdx) => {
                if (
                  cell.type === "textarea" &&
                  !((cell.content as any)?.value || "").trim()
                ) {
                  textareaIndices.push(`${bIdx}-${lIdx}-${cIdx}`);
                }

                if (
                  cell.type === "feedback" &&
                  ((cell.content as any)?.value || "").trim().length > 0
                ) {
                  textareaIndices.push(`${bIdx}-${lIdx}-${cIdx}`);
                }

                if (
                  cell.type === "percentage" &&
                  !(
                    (cell.content as any)?.value &&
                    (!(cell.content as any)?.warnIfZero ||
                      Number((cell.content as any)?.value) > 0)
                  )
                ) {
                  textareaIndices.push(`${bIdx}-${lIdx}-${cIdx}`);
                }
              });
            }
          });
        }
      });
      setInvalidTextareaIndexList(textareaIndices);

      if (textareaIndices.length > 0) {
        const [first] = textareaIndices;
        const blockIndex = Number(first.split("-")[0]);
        if (setMoveToIndex) {
          setMoveToIndex(blockIndex);
        }

        const el = document.getElementById(`textarea-${first}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });

          const inputEl = el.querySelector(
            "textarea, input[type='text'], input[type='number'], input[type='tel'], .ql-editor"
          );

          focusElement((inputEl || el) as HTMLElement);
        }
      }
    }
  };

  const { mutate: saveSelfCheckData, isLoading: isSelfCheckLoading } =
    useMutation(saveSelfCheckQueryFn, {
      onSuccess: () => {
        queryClient.invalidateQueries([
          DASHBOARD_DOMAIN,
          DASHBOARD_DETAIL_ENDPOINT,
        ]);
      },
    });

  const saveNewThoughtRecordDataWithRawRecord =
    useSaveThoughtRecordWithRawContent({
      percentageThought:
        (getReferenceData(data || [], "percentageThought") as PercentageType)
          ?.percentage || 0,
      date: getReferenceData(data || [], "date")?.value || "",
      situation: getReferenceData(data || [], "situation")?.value || "",
      situation_indirect:
        getReferenceData(data || [], "situation_indirect")?.value || "",
      situation_direct:
        getReferenceData(data || [], "situation_direct")?.value || "",
    });

  const saveNewMeditationRecordDataWithRawRecord =
    useSaveMeditationRecordWithRawContent({
      type: getReferenceData(data || [], "meditation_type")?.value || "",
    });

  const submit = useSubmitImpactScore({
    taskKey: taskKey,
    data: data || [],
    automaticThoughtId: automaticThoughtId,
  });

  const [isDoneModalOpen, setModalOpen] = useState<boolean>(false);
  const [isUndoneModalOpen, setUndoneModalOpen] = useState<boolean>(false);

  const refetchTaskList = useRefetchTaskList();

  const refetchTaskListAndOpenSidebar = () => {
    setDirty(false);
    refetchTaskList();
    checkModalOpenAfterDone();
  };

  const checkModalOpenAfterDone = () => {
    if (isLastDone) {
      setModalOpen(true);
    } else {
      removeTab();
    }
  };

  const { mutate: activityDone, isLoading: isActivityDoneLoading } =
    useMutation(() => taskApi.done(`${taskKey}`), {
      onSuccess: () => {
        setIsTmpDisabled(true);

        refetchTaskListAndOpenSidebar();

        // queryClient.invalidateQueries([HOME_DOMAIN, HOME_TASK_LIST_ENDPOINT]);
        if (getTaskIdFromTaskKey(taskKey) === "8-6-A") {
          queryClient.invalidateQueries([
            HOME_DOMAIN,
            HOME_MEDITATION_LIST_ENDPOINT,
          ]);
        }
      },
    });

  const { mutate: activityUndone, isLoading: isActivityUndoneLoading } =
    useMutation(() => taskApi.undone(`${taskKey}`), {
      onSuccess: () => {
        refetchTaskList();
        if (getTaskIdFromTaskKey(taskKey) === "8-6-A") {
          queryClient.invalidateQueries([
            HOME_DOMAIN,
            HOME_MEDITATION_LIST_ENDPOINT,
          ]);
        }
        setTaskListState((prev) =>
          prev.map((task) =>
            task.taskKey === taskKey
              ? { ...task, isDone: false, isOpen: true }
              : task
          )
        );
        onUndoneSuccess && onUndoneSuccess();
      },
    });

  const { mutate: thoughtRecordDone, isLoading: isThoughtRecordDoneLoading } =
    useMutation(() => thoughtRecordApi.done(`${thoughtRecordKey}`), {
      onSuccess: () => {
        setIsTmpDisabled(true);

        queryClient.invalidateQueries([
          THOUGHTRECORD_DOMAIN,
          THOUGHTRECORD_LIST_ENDPOINT,
        ]);

        refetchTaskListAndOpenSidebar();

        if (isFirstDone) {
          saveNewMeditationRecordDataWithRawRecord();
        }
      },
    });

  const {
    mutate: thoughtRecordUndone,
    isLoading: isThoughtRecordUndoneLoading,
  } = useMutation(() => thoughtRecordApi.undone(`${thoughtRecordKey}`), {
    onSuccess: () => {
      queryClient.invalidateQueries([
        THOUGHTRECORD_DOMAIN,
        THOUGHTRECORD_LIST_ENDPOINT,
      ]);

      refetchTaskList();

      onUndoneSuccess && onUndoneSuccess();
    },
  });

  const {
    mutate: meditationRecordDone,
    isLoading: isMeditationRecordDoneLoading,
  } = useMutation(() => meditationRecordApi.done(`${meditationRecordKey}`), {
    onSuccess: () => {
      setIsTmpDisabled(true);

      queryClient.invalidateQueries([
        MEDITATION_RECORD_DOMAIN,
        MEDITATION_RECORD_LIST_ENDPOINT,
      ]);

      refetchTaskListAndOpenSidebar();

      if (isFirstDone) {
        saveNewThoughtRecordDataWithRawRecord();
      }
    },
  });

  const {
    mutate: meditationRecordUndone,
    isLoading: isMeditationRecordUndoneLoading,
  } = useMutation(() => meditationRecordApi.undone(`${meditationRecordKey}`), {
    onSuccess: () => {
      queryClient.invalidateQueries([
        MEDITATION_RECORD_DOMAIN,
        MEDITATION_RECORD_LIST_ENDPOINT,
      ]);

      refetchTaskList();

      onUndoneSuccess && onUndoneSuccess();
    },
  });

  const undoneAction = () => {
    if (isThoughtRecord) {
      thoughtRecordUndone();
    } else if (isMeditationRecord) {
      meditationRecordUndone();
    } else {
      activityUndone();
    }
  };

  const doneAction = () => {
    if (data) {
      setDirty(false);

      const modalContent = getDoneModalContent({
        lastTaskUpdatedAt,
        now: new Date(),
      });
      setDoneModalContent(modalContent);

      if (isSelfCheck) {
        saveSelfCheckData();
      }
      submit();

      if (isThoughtRecord) {
        thoughtRecordDone();
      } else if (isMeditationRecord) {
        meditationRecordDone();
      } else {
        activityDone();
      }
    }
  };

  useEffect(() => {
    setIsLoading(
      isActivityDoneLoading ||
        isThoughtRecordDoneLoading ||
        isMeditationRecordDoneLoading ||
        isSelfCheckLoading
    );
  }, [
    isActivityDoneLoading,
    isThoughtRecordDoneLoading,
    isMeditationRecordDoneLoading,
    isSelfCheckLoading,
    setIsLoading,
  ]);

  useEffect(() => {
    setIsTmpDisabled(false);
    return () => {
      setIsTmpDisabled(false);
    };
  }, []);

  const handleDoneModalAction = () => {
    setModalOpen(false);
    removeTab();
  };

  const handleUndoneModalAction = () => {
    undoneAction();
    setUndoneModalOpen(false);
  };

  // useEffect(() => {
  //   if (isPatient) {
  //     setDirty(!isDone);
  //   }
  // }, [isPatient, isDone]);

  // const handleConfirmNavigation = () => {
  //   if (blocker.state === "blocked") {
  //     blocker.proceed();
  //   }
  // };
  //
  // const handleCancelNavigation = () => {
  //   if (blocker.state === "blocked") {
  //     blocker.reset();
  //   }
  // };

  // useEffect(() => {
  //   if (isPatient && isInappWebview) {
  //     const msg = JSON.stringify({
  //       event: "set_dirty",
  //       params: {
  //         isDirty: isDirty,
  //       },
  //     });
  //     sendMessage(msg);
  //   }
  // }, [isDirty, isPatient, isInappWebview]);

  const button = (
    <Button
      size={size}
      startDecorator={isDone ? <CheckIconSvgDoneButton /> : undefined}
      disabled={disabled || isLoading || isTmpDisabled}
      loading={isLoading}
      loadingIndicator={<CircularProgress color={"neutral"} />}
      onClick={() => {
        if (isDone) {
          setUndoneModalOpen(true);
        } else {
          doneAction();
        }
      }}
      variant={isDone ? "Outlined" : "Solid"}
      sx={{
        ...(isDone && { "--Button-gap": "4px" }),
      }}
    >
      {isDone ? "마침" : "마치기"}
    </Button>
  );

  return (
    <Box sx={{ position: "relative" }}>
      {button}
      {disabled && !isLoading && !isTmpDisabled && (
        <Box
          onClick={highlightUnfilledBlocks}
          sx={{ position: "absolute", inset: 0 }}
        />
      )}
      {/*<CustomModal*/}
      {/*  open={blocker.state === "blocked"}*/}
      {/*  onClose={handleCancelNavigation}*/}
      {/*  title={"활동지를 마치지 않았어요"}*/}
      {/*  subtitle={"마무리 짓지 않고 여기서 그만할까요?"}*/}
      {/*  actions={[*/}
      {/*    {*/}
      {/*      label: "마치지 않고 나가기",*/}
      {/*      onClick: handleConfirmNavigation,*/}
      {/*      variant: "Soft",*/}
      {/*      color: "danger",*/}
      {/*    },*/}
      {/*    {*/}
      {/*      label: "마저 계속하기",*/}
      {/*      onClick: handleCancelNavigation,*/}
      {/*      variant: "Plain",*/}
      {/*    },*/}
      {/*  ]}*/}
      {/*/>*/}
      <CustomModal
        open={isDoneModalOpen}
        onClose={handleDoneModalAction}
        title={doneModalContent.title}
        subtitle={doneModalContent.subtitle}
        actions={[
          {
            label: "확인",
            onClick: handleDoneModalAction,
            variant: "Solid",
          },
        ]}
      />
      <CustomModal
        open={isUndoneModalOpen}
        onClose={() => {
          setUndoneModalOpen(false);
        }}
        title="활동지를 다시 열까요?"
        subtitle="활동지를 다시 열어 내용을 수정할 수 있어요. 끝나면 꼭 ‘마치기’를 눌러주세요."
        actions={[
          {
            label: "다시열기",
            onClick: handleUndoneModalAction,
            variant: "Solid",
          },
        ]}
      />
    </Box>
  );
}
